import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@mui/material';
import { getSingleEventList, destroyEvent, joinMeeting, createEventMeeting } from '@/api/event.api';
import { successHandler } from '@/utils/toastHandler';
import { dmpMeetingFailure, hasDmpMeeting, isDmpMeetingJoinTime } from '@/utils/dmpMeeting';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import useCurrentLang from '@/hooks/useCurrentLang';
import { H2, H4 } from '@/components/UI/Typography/Heading';
import { Button } from '@/components/UI/Button';
import { ModalMainWrapper, Row, ActionBtns, ConfirmWrapper, ConfirmTitle, DeleteBtns } from './style';
import { Close } from '@/components/Icon';
import Loading from '@/components/UI/Loading';
import { Text } from '@/components/UI/Typography/Text';

interface ModalProps {
  open: boolean;
  id: number;
  handleClose: () => void;
  handleDeleteData: (id: number) => void;
}

const EventModal = ({ open, id, handleClose, handleDeleteData }: ModalProps) => {
  const { t, i18n } = useTranslation();
  const lang = useCurrentLang();
  const english = lang === 'en';
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const [data, setData] = useState<EventFormFields | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);
  const locked = useRef(false);
  const meetingId = hasDmpMeeting(data?.vc) ? data!.vc!.meetingID : null;
  const canCreateMeeting = Boolean(data && Number(data.evt_host_id) > 0 && Number(data.evt_host_id) === Number(userData?.id));

  useEffect(() => {
    if (!open || id < 1) return;
    let current = true;
    setData(null); setIsLoading(true); setLoadError(''); setActionError('');
    void getSingleEventList(id).then((response) => {
      if (!current) return;
      if (!response.data.success) throw { response: { data: response.data } };
      setData(response.data.data);
    }).catch((error) => {
      if (current) setLoadError(dmpMeetingFailure(error, english));
    }).finally(() => { if (current) setIsLoading(false); });
    return () => { current = false; };
  }, [id, open, reload, english, userData?.id]);

  const deleteHandler = async () => {
    if (!data || locked.current) return;
    locked.current = true; setBusy(true); setActionError('');
    try {
      await destroyEvent(data.id);
      handleDeleteData(data.id); handleClose(); setOpenDelete(false);
    } catch (error) { setActionError(dmpMeetingFailure(error, english)); }
    finally { locked.current = false; setBusy(false); }
  };

  const openMeeting = async () => {
    if (!meetingId || !data || locked.current) return;
    if (!isDmpMeetingJoinTime(data.evt_startDate)) { setActionError(t('error.joinMeeting')); return; }
    locked.current = true; setBusy(true); setActionError('');
    try {
      const params = new URLSearchParams({ meetingID: meetingId, locale: i18n.language });
      const response = (await joinMeeting(params.toString())).data;
      if (!response.success || typeof response.data !== 'string') throw { response: { data: response } };
      if (!['https:', 'http:'].includes(new URL(response.data).protocol)) throw new Error('Invalid meeting URL');
      window.open(response.data, '_blank', 'noopener,noreferrer');
    } catch (error) { setActionError(dmpMeetingFailure(error, english)); }
    finally { locked.current = false; setBusy(false); }
  };

  const repairMeeting = async () => {
    if (!data || !canCreateMeeting || locked.current) return;
    locked.current = true; setBusy(true); setActionError('');
    try {
      const response = (await createEventMeeting(data.id)).data;
      if (!response.success || !hasDmpMeeting(response.data)) throw { response: { data: response } };
      setReload((previous) => previous + 1);
    } catch (error) { setActionError(dmpMeetingFailure(error, english)); }
    finally { locked.current = false; setBusy(false); }
  };

  const shareToGuest = async () => {
    if (!data || !meetingId) return;
    if (!isDmpMeetingJoinTime(data.evt_startDate)) { setActionError(t('error.joinMeeting')); return; }
    try {
      await navigator.clipboard.writeText(window.location.origin + '/event/joinMeeting?meetingID=' + encodeURIComponent(meetingId));
      successHandler(t('success.copyMeetingLink'));
    } catch {
      setActionError(english ? 'Unable to copy the meeting link. Please retry.' : '無法複製會議連結，請重試。');
    }
  };

  return <>
    <Modal open={open} onClose={handleClose} aria-labelledby="event-modal-title">
      <ModalMainWrapper>
        <Close.ButtonClose type="button" onClick={handleClose} aria-label={t('buttons.cancel')}><Close.CloseSVG /></Close.ButtonClose>
        <div className="event-modal-body">
          {isLoading ? <Loading /> : loadError || !data ? <>
            <p role="alert">{loadError}</p>
            <Button type="button" variant="primary-invert" onClick={() => setReload((previous) => previous + 1)}>{english ? 'Retry' : '重試'}</Button>
          </> : <>
            <H2 id="event-modal-title">{data.evt_name}</H2>
            <H4 style={{ color: '#666666', marginBottom: 10 }}>{data.evt_startDate}</H4>
            <Row><div className="icon"><img src="/images/event/modal/handshake.svg" alt="" /></div><H4>{data.deal?.name || '-'}</H4></Row>
            <Row>
              <div className="icon"><img src="/images/event/modal/interpreter.svg" alt="" /></div>
              {meetingId ? <Button type="button" variant="primary-invert" disabled={busy} onClick={() => void openMeeting()}>{t('buttons.joinMeeting')}</Button> : <div>
                <Text>{english ? 'This event has no valid video meeting connection.' : '此活動尚未建立有效的視訊會議連線。'}</Text>
                {canCreateMeeting ? <Button type="button" variant="primary" disabled={busy} onClick={() => void repairMeeting()}>
                  {busy ? english ? 'Creating meeting…' : '正在建立會議…' : english ? 'Create meeting connection' : '建立會議連線'}
                </Button> : <Text>{english ? 'Please contact the host to create the meeting connection.' : '請聯絡主持人建立會議連線。'}</Text>}
              </div>}
            </Row>
            <Row><div className="icon"><img src="/images/event/modal/chat.svg" alt="" /></div><H4>{data.evt_note}</H4></Row>
            {actionError && <Text role="alert" color="#9f1b1b">{actionError}</Text>}
            <ActionBtns>
              <button type="button" disabled={busy} onClick={() => navigate('/event/newEvent?edit=' + data.id)}>{t('buttons.edit')}</button>
              <button type="button" disabled={busy} onClick={() => setOpenDelete(true)}>{t('buttons.delete')}</button>
              {meetingId && <button type="button" disabled={busy} onClick={() => void shareToGuest()}>{t('buttons.share')}</button>}
            </ActionBtns>
          </>}
        </div>
      </ModalMainWrapper>
    </Modal>
    <Modal open={openDelete} onClose={() => setOpenDelete(false)} aria-labelledby="event-delete-title">
      <ModalMainWrapper><div className="event-modal-body">
        <ConfirmWrapper><ConfirmTitle id="event-delete-title">{t('pages.event.modal.title')}</ConfirmTitle>{t('pages.event.modal.content')}</ConfirmWrapper>
        <DeleteBtns>
          <Button type="button" variant="primary" size="lg" disabled={busy} onClick={() => void deleteHandler()}>{t('buttons.delete')}</Button>
          <button type="button" disabled={busy} onClick={() => setOpenDelete(false)}>{t('buttons.cancel')}</button>
        </DeleteBtns>
      </div></ModalMainWrapper>
    </Modal>
  </>;
};
export default EventModal;
